import { useAdminAuditLogs } from '@hakimo-ui/hakimo/data-access';
import { Page } from '@hakimo-ui/hakimo/ui-layout';
import { Pagination, Table, TableData } from '@hakimo-ui/hakimo/ui-table';
import {
  useDebounce,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import { InputField } from '@hakimo-ui/shared/ui-base';
import { useEffect, useMemo, useState } from 'react';
import { AdminAuditLogFilters, defaultFilters } from './types';
import { PAGE_SIZE, columns, getSearchParams, getTableData } from './util';

function AdminAuditLogListComponent() {
  const [filters, setFilters] = useState<AdminAuditLogFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchValue, setSearchValue] = useState(defaultFilters.tenantName);
  const [tableData, setTableData] = useState<TableData>({
    columns,
    rows: [],
  });

  const debouncedSearchValue = useDebounce(searchValue, 400);

  const searchParams = useMemo(
    () => getSearchParams(filters, page, PAGE_SIZE),
    [filters, page]
  );

  const { data, isLoading } = useAdminAuditLogs(searchParams);

  useEffect(() => {
    setFilters({ tenantName: debouncedSearchValue });
    setPage(1);
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (data) {
      setTableData(getTableData(data.items));
      setTotal(data.total);
    }
  }, [data]);

  const fromIndex = (page - 1) * PAGE_SIZE;
  const toIndex = Math.min(fromIndex + PAGE_SIZE - 1, total - 1);

  const tableFooter = (
    <div className="dark:bg-dark-bg border-t bg-white p-4 dark:border-gray-800">
      <Pagination
        from={fromIndex + 1}
        to={toIndex + 1}
        pageSize={PAGE_SIZE}
        total={total}
        disabled={isLoading}
        onChangePage={setPage}
      />
    </div>
  );

  return (
    <Page title="Admin Audit Log" titleFontSize="large">
      <div className="flex h-full flex-col px-4 py-4">
        <div className="max-w-md">
          <InputField
            label=""
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="search"
            placeholder="Search by tenant name..."
          />
        </div>
        <div className="dark:bg-dark-bg mt-4 flex min-h-0 flex-1 flex-col justify-start overflow-y-hidden rounded-t-xl">
          <Table
            data={tableData}
            loading={isLoading}
            footer={tableFooter}
            scrollResetKey={page}
          />
        </div>
      </div>
    </Page>
  );
}

export const AdminAuditLogList = withAuthz(
  withErrorBoundary(AdminAuditLogListComponent),
  ['admin-audit-log:view']
);

export default AdminAuditLogList;
